import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import {
  CircularProgress,
  Container,
  Grid,
  Typography,
  Button,
  styled,
  AlertColor
} from '@mui/material';
import ButtonWithSpinner from 'src/components/buttons/button-with-spinner';
import { MultiSelect } from 'react-multi-select-component';
import { MatomoEventTrackMapObject } from '../../../../analytics/simulator/constants/constant';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterLoadingSkeleton from 'src/components/skeletons/filtersLoadingSkeleton';
import Storage from 'src/utils/storage/storage';
import { setSourceId } from 'src/components/user/user/store/user-slice';
import { ISelectOption } from 'src/lib';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import {
  fetchCards,
  getModuleIdData,
  getModulesList,
  getSegmentDetails,
  getSourceList,
  getUserGuide,
  getHomePageMetaData,
  setActiveBottomTab
} from '../../store/landing-slice';
import SearchBar from '../search-bar/search-bar';
import LinksContainerCard from '../links-container-card/links-container-card';
import {
  ISKUObjectNew2,
  ISkuObject2
} from '../../../simulator/innovation/types/sku-popup-model';
import './landing.scss';
import TemporaryDialog from '../../../simulator/common/components/module-save-button/temporary-dialog';
import SnackbarComponent from '../../../simulator/innovation/components/snackbar/snackbar';
import HomePageContentComponet from './home-page-content-component';
import HomepageBase from './homepage-content-v2/homepage-base';
import BottomTabs from './homepage-content-v2/bottom-tabs';
import { getPhRecommendationConfig } from '../../../simulator/scenarios/store/scenario-slice';

const StyledGrid = styled(Grid)`
  background-image: url('/static/images/simulation-bg.svg');
  background-position: right top;
  background-repeat: no-repeat;
`;
/**
 * Renders the cards by fetching data from backend.
 * The fetched data contains information about how many cards to render, links, tabs inside the card and the order of rendering
 * @returns The landing page
 */

interface ImageRendererProps {
  src: string;
  alt: string;
  width?: string;
  height?: string;
}
const ImageRenderer: React.FC<ImageRendererProps> = ({
  src,
  alt,
  width,
  height
}) => (
  <img
    src={src}
    alt={alt}
    width={width}
    height={height}
    style={{ objectFit: 'contain', borderRadius: '12px' }}
  />
);

export default function LandingWrapper() {
  const { pushInstruction } = useMatomo();
  const appDispatch = useAppDispatch();

  const navigate = useNavigate();

  const {
    fetchingSourceList,
    sourceFilterData,
    isUserGuideDownloading,
    fetchingHomePageMetaData,
    homePageMetaData
  } = useAppSelector((state) => state.landing);
  const { callFunction } = useMatomoFunction();

  document.title = 'RGM';

  // local state for source value
  const [selectedSource, setSourceValue] = useState([]);

  const [isTemporaryDialogOpen, setIsTemporaryDialogOpen] = useState(false);

  // local state for message
  const [infoMessage, setInfoMessage] = useState(
    'Select Country and Segment from dropdown and Click ‘Proceed’'
  );
  const [isInfoMessageVisibile, setInfoMessageVisibility] = useState(false);

  // local state for snackbar open/close
  const [isSnackbarOpen, setSnackbar] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: '',
    snackbarType: 'success'
  });
  const getPageName = () => {
    const page_title = window.location.href.split('/').at(-1);
    return page_title;
  };
  useEffect(() => {
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );
  }, []);
  useEffect(() => {
    const { userMeta } = Storage.getUserMetadata();
    // console.log('matomo username = ', userMeta?.user_name);
    callFunction('track_user_details');
    // pushInstruction('setUserId', userMeta?.user_name);
  }, []);

  useEffect(() => {
    // appDispatch(getModulesList()).then((res) => {

    // })
    // to reset the activeBottomTab (if any popup is opened on homepage initially)
    appDispatch(setActiveBottomTab(''));
    appDispatch(getSourceList()).then((res) => {
      // const response = res.payload as { data: ISkuObject2 };
      const response = res.payload as { data: ISKUObjectNew2 };
      // setting the default source id as per the value in local storage
      const sourceIdFromLocalStorage =
        Storage.getSourceId().source || response.data[0].default_value.value;
      const defaultSourceId = response.data[0].values.filter(
        (e) => e.value === sourceIdFromLocalStorage
      );
      setSourceValue(defaultSourceId);

      // showing the notification about the selected segment
      setSnackbarContent({
        snackbarText: `${defaultSourceId[0].label}-Segment has been activated`,
        snackbarType: 'success'
      });
      setSnackbar(true);

      // calling the segment api as per the selected segment
      // appDispatch(getSegmentDetails(sourceIdFromLocalStorage));
      appDispatch(getSegmentDetails(sourceIdFromLocalStorage)).then(
        (segmentDetailsData) => {
          // storing the segment details in local storage
          Storage.setSegmentDetails(segmentDetailsData);
        }
      );

      // calling the get module ids data
      appDispatch(getModuleIdData()).then((moduleIdData) =>
        Storage.setModuleIdData(moduleIdData)
      );

      // calling the Home Page meta data API
      // TODO: clear the code after homepage modification
      // appDispatch(getHomePageMetaData(sourceIdFromLocalStorage));

      // on load getting the ph recommendation config
      appDispatch(getPhRecommendationConfig(sourceIdFromLocalStorage)).then(
        (phRecResponse) => {
          Storage.setPhRecommendationLegend(phRecResponse);
        }
      );
    });
  }, []);

  // renderer function for single select
  const customSingleRender = ({ checked, option, onClick, disabled }) => (
    <div className={`item-renderer ${disabled && 'disabled'}`}>
      <input
        type='radio'
        onChange={onClick}
        checked={checked}
        tabIndex={-1}
        disabled={disabled}
      />
      {/* <img src={option.icon} alt="" style={{ width: '20px', margin: '4px 8px 0px 0px' }} /> */}
      <span onChange={onClick}>{option.label}</span>
    </div>
  );

  // renderer function for displaying selected values
  const customValueRender = (selected: ISelectOption[]) =>
    selected.map((option) => (
      <div key={option.value} style={{ display: 'flex' }}>
        <ImageRenderer
          src={`/media/${option.icon}`}
          alt='flag'
          width='20px'
          height='20px'
        />
        <span style={{ width: '20px', margin: '2px 0px 0px 12px' }}>
          {option.label}
        </span>
      </div>
    ));

  /**
   * handler for changing source id
   */
  const handleChange = (selectedSourceValue) => {
    const currentSourceDropdownValue = selectedSourceValue.slice(-1);
    // setting the source value
    setSourceValue(currentSourceDropdownValue);

    // setting the isInfoMessageVisibile to true only if it is changed from active segment
    if (Storage.getSourceId().source !== currentSourceDropdownValue[0].value) {
      setInfoMessageVisibility(true);
    } else {
      setInfoMessageVisibility(false);
    }
  };

  /**
   * handler for submit source id
   */
  const handleSubmit = () => {
    // set source id into slice of user
    // eslint-disable-next-line dot-notation
    appDispatch(setSourceId(selectedSource[0]['value']));

    // setting the isInfoMessageVisibile to false on submit
    setInfoMessageVisibility(false);

    // reloading the page once source id is set on click of proceed
    window.location.href = '/';
  };

  /**
   * handler for download user guide
   */
  // eslint-disable-next-line arrow-body-style
  const handleDownload = () => {
    return appDispatch(getUserGuide())
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.error('Failed to download summary', error);
        setIsTemporaryDialogOpen(true);
      });
  };

  const containerStyle: React.CSSProperties = {
    backgroundImage: 'url(/static/images/homepage_background_v2.png)',
    backgroundSize: 'cover', // Ensures the image covers the entire container
    backgroundPosition: 'center', // Centers the image
    backgroundRepeat: 'no-repeat', // Prevents the image from repeating
    width: '100%', // Takes full width of the parent
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly'
  };

  return (
    <Container
      maxWidth={false}
      sx={{ position: 'relative', padding: '0px !important', width: '100%' }}
    >
      <div style={containerStyle} className='landing-container'>
        <div className='ppa-icon-img'>
          <img
            src='/static/images/ppa_analytics_edge2.svg'
            alt='ppa_analytic_edge'
            className='ppa-icon-img-div'
          />
        </div>
        {/* grid containing main title and segment selection dropdown */}
        <Grid
          container
          spacing={1}
          sx={{
            marginBottom: '10px'
          }}
        >
          <Grid item xs={3} />
          <Grid item xs={6} alignItems='center' sx={{ margin: 'auto' }}>

            <div className='landing-title'>
              <Typography variant='h1' color='#393939' alignItems='center'>
                <span className='gradient-text'>
                  {' '}
                  Every Day Great Execution
                </span>
              </Typography>
              <Typography variant='h3' color='#393939' align='left'>
                starts here...
              </Typography>
            </div>
            {/* segment selection dropdown */}
            <div style={{ width: '60%', margin: 'auto', paddingTop: '10px' }}>
              <div className='segment-master-div'>
                <div className='segment-div'>
                  <div className='filter-title-div'>
                    <div className='filter-title-img'>
                      <img
                        src='/static/images/world-icon.svg'
                        alt='title-icon'
                      />
                    </div>
                    <div className='filter-title'>Choose Country x Segment</div>
                  </div>
                  <div className='filter-master-div'>
                    <div className='filter-div'>
                      <MultiSelect
                        options={
                          sourceFilterData.length > 0
                            ? (sourceFilterData[0].values as ISelectOption[])
                            : [{ label: '', value: '' }]
                        }
                        value={selectedSource}
                        hasSelectAll={false}
                        onChange={(val) => handleChange(val)}
                        closeOnChangedValue
                        labelledBy='Select'
                        className='multi-select-div'
                        ItemRenderer={customSingleRender}
                        valueRenderer={customValueRender}
                      />
                    </div>
                    <div className='button-box'>
                      <Button
                        type='submit'
                        variant='contained'
                        onClick={handleSubmit}
                        sx={{ height: '30px', padding: '0' }}
                      >
                        <img
                          src='/static/images/right-arrow-icon.svg'
                          alt='icon'
                        />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Grid>
          <Grid item xs={3} />
        </Grid>

        {/* homepage content */}
        {fetchingSourceList === false && sourceFilterData.length > 0 ? (
          <div
            className='sections-div'
          // style={{ height: '60%' }}
          >
            <HomepageBase />
            {/* bottom tabs */}
            {/* <div className='bottom-tabs-div'>
              <BottomTabs />
            </div> */}
          </div>
        ) : (
          <div style={{ margin: 'auto' }}>{FilterLoadingSkeleton(1)}</div>
        )}

        {isTemporaryDialogOpen ? (
          <TemporaryDialog
            isOpen={isTemporaryDialogOpen}
            message='Failed to download user guide'
            onClose={() => {
              setIsTemporaryDialogOpen(false);
            }}
          />
        ) : null}
        <SnackbarComponent
          isSnackbarOpen={isSnackbarOpen}
          setSnackbar={setSnackbar}
          snackbarText={snackbarContent.snackbarText}
          snackbarType={snackbarContent.snackbarType}
        />
        {/* bottom tabs */}
        {fetchingSourceList === false && sourceFilterData.length > 0 && (
          <div className='bottom-tabs-div'>
            <BottomTabs />
          </div>
        )}
      </div>
    </Container>
  );
}
